import React, { useState, useMemo } from 'react';
import { 
  Search, 
  BookOpen, 
  Compass, 
  ClipboardList, 
  RefreshCw, 
  Boxes, 
  FolderTree, 
  ShieldCheck, 
  Tv, 
  FileText, 
  Cpu, 
  HelpCircle, 
  CheckCircle2, 
  AlertCircle, 
  ShieldAlert, 
  ListOrdered, 
  Lightbulb, 
  ChevronDown, 
  ChevronUp, 
  Printer, 
  X,
  Sparkles,
  Layers,
  ArrowRight,
  Info
} from 'lucide-react';
import { HelpController } from './help/help.controller';
import { HelpCategoryType, HelpTopic, HelpRule } from './help/help.types';

export default function HelpView() {
  const [selectedCategory, setSelectedCategory] = useState<HelpCategoryType>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedTopics, setExpandedTopics] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<'MANUAL' | 'FAQ'>('MANUAL');

  const categories = useMemo(() => HelpController.getCategories(), []);
  const stats = useMemo(() => HelpController.getStats(), []);
  
  const filteredTopics = useMemo(() => {
    return HelpController.filterTopics(selectedCategory, searchQuery);
  }, [selectedCategory, searchQuery]);

  const filteredFAQs = useMemo(() => {
    return HelpController.filterFAQs(searchQuery);
  }, [searchQuery]);

  const toggleTopic = (id: string) => {
    setExpandedTopics(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const expandAll = () => {
    const all: Record<string, boolean> = {};
    filteredTopics.forEach(t => {
      all[t.id] = true;
    });
    setExpandedTopics(all);
  };

  const collapseAll = () => {
    setExpandedTopics({});
  };

  const handlePrint = () => {
    window.print();
  };

  const renderIcon = (name: string, size = 18, className = '') => {
    switch (name) {
      case 'Compass': return <Compass size={size} className={className} />;
      case 'ClipboardList': return <ClipboardList size={size} className={className} />;
      case 'RefreshCw': return <RefreshCw size={size} className={className} />;
      case 'Boxes': return <Boxes size={size} className={className} />;
      case 'FolderTree': return <FolderTree size={size} className={className} />;
      case 'ShieldCheck': return <ShieldCheck size={size} className={className} />;
      case 'Tv': return <Tv size={size} className={className} />;
      case 'FileText': return <FileText size={size} className={className} />;
      case 'Cpu': return <Cpu size={size} className={className} />;
      default: return <BookOpen size={size} className={className} />;
    }
  };

  const renderRuleBadge = (type: HelpRule['type']) => {
    switch (type) {
      case 'RESTRICTION':
        return <span className="badge bg-danger-subtle text-danger border border-danger font-mono text-xxs uppercase fw-bold">Trava Obrigatória</span>;
      case 'SECURITY':
        return <span className="badge bg-dark text-warning border border-warning font-mono text-xxs uppercase fw-bold">Segurança & RBAC</span>;
      case 'CALCULATION':
        return <span className="badge bg-primary-subtle text-primary border border-primary font-mono text-xxs uppercase fw-bold">Fórmula Automática</span>;
      case 'AUTOMATION':
        return <span className="badge bg-success-subtle text-success border border-success font-mono text-xxs uppercase fw-bold">Automação de Sistema</span>;
      default:
        return <span className="badge bg-secondary font-mono text-xxs uppercase">Regra</span>;
    }
  };

  return (
    <div className="fade-in pb-5" id="help-page-container">
      {/* Header Principal com Resumo e Estatísticas */}
      <div className="bg-white border rounded-3 p-4 shadow-sm mb-4">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 border-bottom pb-3 mb-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="badge bg-primary text-white font-mono text-xs px-2.5 py-1 rounded">
                <BookOpen size={13} className="me-1" /> MANUAL OPERACIONAL v2.6
              </span>
              <span className="badge bg-light text-secondary border font-mono text-xs">
                Base de Conhecimento & Treinamento
              </span>
            </div>
            <h1 className="h3 fw-bold text-dark font-display mb-1">
              Central de Documentação e Treinamento
            </h1>
            <p className="text-secondary text-sm mb-0">
              Guia completo de operação, regras de negócio, esteira produtiva (PCP), integração ERP e catálogo técnico.
            </p>
          </div>

          <div className="d-flex gap-2 align-self-stretch align-self-md-auto">
            <button 
              onClick={handlePrint}
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1.5 shadow-xs"
              title="Imprimir ou Salvar Manual em PDF"
            >
              <Printer size={15} /> Imprimir Manual
            </button>
          </div>
        </div>

        {/* Estatísticas Rápidas da Base de Conhecimento */}
        <div className="row g-2 font-mono">
          <div className="col-6 col-md-3">
            <div className="p-2.5 bg-light rounded border text-center">
              <span className="text-muted text-xxs text-uppercase d-block fw-semibold">Módulos Cobertos</span>
              <strong className="text-dark fs-5">{categories.length - 1} Módulos</strong>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="p-2.5 bg-light rounded border text-center">
              <span className="text-muted text-xxs text-uppercase d-block fw-semibold">Tópicos e Guias</span>
              <strong className="text-primary fs-5">{stats.totalTopics} Tópicos</strong>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="p-2.5 bg-light rounded border text-center">
              <span className="text-muted text-xxs text-uppercase d-block fw-semibold">Regras & Travas</span>
              <strong className="text-danger fs-5">{stats.totalRules} Regras</strong>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="p-2.5 bg-light rounded border text-center">
              <span className="text-muted text-xxs text-uppercase d-block fw-semibold">Dúvidas Frequentes</span>
              <strong className="text-success fs-5">{stats.totalFAQs} FAQs</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Barra de Pesquisa Inteligente */}
      <div className="card shadow-sm border-0 rounded-3 mb-4 overflow-hidden">
        <div className="card-body p-3 bg-white">
          <div className="input-group input-group-lg shadow-xs border rounded-3 overflow-hidden">
            <span className="input-group-text bg-white border-0 text-muted ps-3">
              <Search size={20} className="text-primary" />
            </span>
            <input
              type="text"
              className="form-control border-0 shadow-none text-dark fs-6"
              placeholder="Pesquisar por regras, botões, rotas, termos (ex: ERP, apontamento, roteiro, rate limit, zoom, permissões)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button 
                className="btn btn-link text-muted pe-3 text-decoration-none"
                onClick={() => setSearchQuery('')}
                title="Limpar pesquisa"
              >
                <X size={18} />
              </button>
            )}
          </div>

          {/* Feedback de Busca */}
          {searchQuery && (
            <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top text-xs text-muted">
              <span>
                Exibindo resultados para: <strong className="text-dark">"{searchQuery}"</strong>
              </span>
              <span className="badge bg-primary text-white font-mono">
                {filteredTopics.length} tópico(s) encontrado(s)
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Abas Principais: Manual Completo vs FAQ */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <ul className="nav nav-pills gap-2">
          <li className="nav-item">
            <button
              className={`nav-link px-3 py-2 fw-bold text-xs font-mono d-flex align-items-center gap-2 ${activeTab === 'MANUAL' ? 'active bg-primary text-white' : 'bg-white text-secondary border'}`}
              onClick={() => setActiveTab('MANUAL')}
            >
              <BookOpen size={15} /> Módulos & Documentação Detalhada
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link px-3 py-2 fw-bold text-xs font-mono d-flex align-items-center gap-2 ${activeTab === 'FAQ' ? 'active bg-primary text-white' : 'bg-white text-secondary border'}`}
              onClick={() => setActiveTab('FAQ')}
            >
              <HelpCircle size={15} /> Perguntas Frequentes (FAQ)
            </button>
          </li>
        </ul>

        {activeTab === 'MANUAL' && (
          <div className="d-flex gap-2">
            <button
              onClick={expandAll}
              className="btn btn-sm btn-light border text-secondary font-mono text-xxs d-flex align-items-center gap-1"
            >
              <ChevronDown size={13} /> Expandir Todos
            </button>
            <button
              onClick={collapseAll}
              className="btn btn-sm btn-light border text-secondary font-mono text-xxs d-flex align-items-center gap-1"
            >
              <ChevronUp size={13} /> Recolher Todos
            </button>
          </div>
        )}
      </div>

      {activeTab === 'MANUAL' ? (
        <div className="row g-3">
          {/* Navegação Lateral por Categorias (Desktop & Mobile) */}
          <div className="col-12 col-lg-3">
            <div className="card shadow-sm border rounded-3 p-3 bg-white sticky-top" style={{ top: '1rem', zIndex: 10 }}>
              <span className="text-xxs text-uppercase font-mono text-muted fw-bold d-block mb-2">
                Navegação por Módulo
              </span>
              <div className="list-group list-group-flush gap-1">
                {categories.map((cat) => {
                  const isActive = selectedCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setSelectedCategory(cat.id);
                        window.scrollTo({ top: 120, behavior: 'smooth' });
                      }}
                      className={`list-group-item list-group-item-action rounded-2 border-0 d-flex align-items-center justify-content-between p-2.5 text-xs fw-semibold transition-all ${
                        isActive 
                          ? 'bg-primary text-white shadow-xs' 
                          : 'text-dark bg-transparent hover:bg-light'
                      }`}
                    >
                      <div className="d-flex align-items-center gap-2 text-start">
                        {renderIcon(cat.iconName, 16, isActive ? 'text-white' : 'text-primary')}
                        <span>{cat.label}</span>
                      </div>
                      {cat.id === 'ALL' ? (
                        <span className={`badge rounded-pill font-mono text-xxs ${isActive ? 'bg-white text-primary' : 'bg-light text-muted border'}`}>
                          {stats.totalTopics}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>

              {/* Box de Apoio Rápido */}
              <div className="mt-4 p-3 bg-light rounded-3 border">
                <div className="d-flex align-items-center gap-2 text-dark fw-bold text-xs mb-1">
                  <Lightbulb size={16} className="text-warning" />
                  <span>Dica de Aprendizado</span>
                </div>
                <p className="text-muted text-xxs mb-0">
                  Cada seção possui um bloco de <strong>"Regras & Travas"</strong> para você entender as validações que o sistema executa automaticamente.
                </p>
              </div>
            </div>
          </div>

          {/* Lista de Tópicos e Conteúdos */}
          <div className="col-12 col-lg-9">
            {filteredTopics.length === 0 ? (
              <div className="card shadow-sm border rounded-3 p-5 text-center bg-white">
                <AlertCircle size={40} className="text-warning mx-auto mb-2" />
                <h5 className="fw-bold text-dark font-display mb-1">Nenhum tópico encontrado</h5>
                <p className="text-muted text-sm mb-3">
                  Nenhum conteúdo corresponde ao termo de busca "{searchQuery}". Tente usar palavras-chave como "ERP", "Pedidos", "Corte", "Usuários" ou "Projetos".
                </p>
                <button 
                  className="btn btn-outline-primary btn-sm mx-auto"
                  onClick={() => { setSearchQuery(''); setSelectedCategory('ALL'); }}
                >
                  Limpar Filtros e Ver Todos
                </button>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {filteredTopics.map((topic) => {
                  const isExpanded = expandedTopics[topic.id] !== false; // Padrão aberto
                  return (
                    <div 
                      key={topic.id} 
                      className="card shadow-sm border rounded-3 overflow-hidden bg-white"
                      id={`topic-${topic.id}`}
                    >
                      {/* Cabeçalho do Card (Clicável para expandir/recolher) */}
                      <div 
                        className="card-header bg-white border-bottom p-3 cursor-pointer d-flex justify-content-between align-items-center"
                        onClick={() => toggleTopic(topic.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div className="p-2 bg-primary-subtle text-primary rounded-3 flex-shrink-0">
                            {renderIcon(topic.iconName, 22)}
                          </div>
                          <div>
                            <div className="d-flex align-items-center gap-2 mb-0.5">
                              <span className="badge bg-secondary-subtle text-secondary font-mono text-xxs fw-semibold">
                                {topic.categoryName}
                              </span>
                              {topic.badge && (
                                <span className="badge bg-warning-subtle text-dark border border-warning font-mono text-xxs fw-bold">
                                  {topic.badge}
                                </span>
                              )}
                            </div>
                            <h5 className="fw-bold text-dark font-display mb-0">
                              {topic.title}
                            </h5>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <button 
                            className="btn btn-light btn-sm border rounded-circle p-1"
                            title={isExpanded ? 'Recolher' : 'Expandir'}
                          >
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </div>
                      </div>

                      {/* Conteúdo Detalhado do Tópico */}
                      {isExpanded && (
                        <div className="card-body p-4">
                          {/* Subtítulo & Visão Geral */}
                          <p className="text-secondary fw-semibold text-sm mb-2">
                            {topic.subtitle}
                          </p>
                          <div className="p-3 bg-light rounded-3 border mb-4 text-sm text-dark">
                            {topic.overview}
                          </div>

                          {/* 1. O que este módulo faz */}
                          <div className="mb-4">
                            <h6 className="fw-bold text-dark font-display d-flex align-items-center gap-2 mb-2 pb-1 border-bottom">
                              <CheckCircle2 size={16} className="text-success" />
                              O que este recurso faz:
                            </h6>
                            <ul className="list-group list-group-flush text-sm">
                              {topic.whatItDoes.map((item, idx) => (
                                <li key={idx} className="list-group-item px-0 py-1.5 border-0 d-flex align-items-start gap-2 bg-transparent text-secondary">
                                  <ArrowRight size={14} className="text-primary flex-shrink-0 mt-1" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* 2. Como Fazer (Passo a Passo) */}
                          <div className="mb-4">
                            <h6 className="fw-bold text-dark font-display d-flex align-items-center gap-2 mb-3 pb-1 border-bottom">
                              <ListOrdered size={16} className="text-primary" />
                              Como Utilizar (Passo a Passo):
                            </h6>
                            <div className="row g-2">
                              {topic.howToDoSteps.map((step) => (
                                <div key={step.number} className="col-12 col-md-6">
                                  <div className="p-3 bg-light rounded-3 border h-100 position-relative">
                                    <span className="badge bg-dark text-white font-mono text-xxs mb-1">
                                      PASSO {step.number}
                                    </span>
                                    <h6 className="fw-bold text-dark font-display text-sm mb-1">
                                      {step.title}
                                    </h6>
                                    <p className="text-muted text-xs mb-0">
                                      {step.description}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* 3. Regras de Negócio, Travas e Validações */}
                          {topic.rulesAndValidations.length > 0 && (
                            <div className="mb-4">
                              <h6 className="fw-bold text-dark font-display d-flex align-items-center gap-2 mb-3 pb-1 border-bottom">
                                <ShieldAlert size={16} className="text-danger" />
                                Regras de Negócio, Fórmulas & Travas de Segurança:
                              </h6>
                              <div className="d-flex flex-column gap-2">
                                {topic.rulesAndValidations.map((rule, idx) => (
                                  <div key={idx} className="p-3 border rounded-3 bg-white border-start border-3 border-start-danger">
                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                      <strong className="text-dark text-sm">{rule.title}</strong>
                                      {renderRuleBadge(rule.type)}
                                    </div>
                                    <p className="text-secondary text-xs mb-0">
                                      {rule.description}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* 4. Dicas e Boas Práticas */}
                          {topic.tipsAndBestPractices.length > 0 && (
                            <div className="p-3 bg-warning-subtle text-dark border border-warning rounded-3 mb-3">
                              <div className="d-flex align-items-center gap-2 fw-bold text-xs mb-2">
                                <Lightbulb size={16} className="text-warning-emphasis" />
                                <span>Recomendações e Boas Práticas de Chão de Fábrica:</span>
                              </div>
                              <ul className="mb-0 ps-3 text-xs text-secondary">
                                {topic.tipsAndBestPractices.map((tip, idx) => (
                                  <li key={idx} className="mb-1">{tip}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Palavras-chave / Tags */}
                          <div className="d-flex flex-wrap gap-1 align-items-center pt-2 border-top">
                            <span className="text-xxs text-muted font-mono uppercase me-1">Termos Relacionados:</span>
                            {topic.keywords.map((kw, idx) => (
                              <span 
                                key={idx} 
                                className="badge bg-light text-muted border font-mono text-xxs cursor-pointer"
                                onClick={() => setSearchQuery(kw)}
                                title="Filtrar por este termo"
                              >
                                #{kw}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Seção FAQ: Perguntas Frequentes */
        <div className="card shadow-sm border rounded-3 p-4 bg-white">
          <div className="border-bottom pb-3 mb-4">
            <h4 className="fw-bold text-dark font-display mb-1">Perguntas Frequentes e Resolução de Problemas</h4>
            <p className="text-muted text-sm mb-0">
              Respostas diretas para as dúvidas operacionais mais recorrentes no dia a dia da fábrica.
            </p>
          </div>

          <div className="d-flex flex-column gap-3">
            {filteredFAQs.map((faq, idx) => (
              <div key={idx} className="p-3 bg-light rounded-3 border">
                <h6 className="fw-bold text-dark font-display d-flex align-items-center gap-2 mb-2">
                  <HelpCircle size={17} className="text-primary flex-shrink-0" />
                  {faq.question}
                </h6>
                <p className="text-secondary text-sm mb-0 ps-4">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
